// src/services/pftExtractionService.js
//
// The admin upload page's view of PFT extraction: live status of every file
// (pft_files) and of the runs (pft_runs), and the "Extract next batch" call. Rules:
// both collections are read-only for the site's admins; only the startPftExtraction
// function and the extraction job write them. Section logic is in
// pftExtractionStatus.js (tested).

import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";
import { getCollectionPath } from "./firestorePaths";
import { authedFetch } from "./authedFetch";
import { FUNCTIONS_BASE } from "../config/functionsBase";

/** Live list of every file's extraction state. Returns the unsubscribe function. */
export const watchPftFiles = (onChange, onError) =>
  onSnapshot(
    collection(db, getCollectionPath("pft_files")),
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );

/** Live list of the 10 most recent runs, newest first. */
export const watchPftRuns = (onChange, onError) =>
  onSnapshot(
    query(collection(db, getCollectionPath("pft_runs")), orderBy("createdAt", "desc"), limit(10)),
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );

/**
 * Start extraction on the next `count` files (100-500). The server picks the files.
 * Resolves to { started, runId?, files?, estimateUsd?, reason? }; throws with a
 * staff-readable message otherwise.
 */
export const startPftExtraction = async ({ tenantId, siteId, count }) => {
  const res = await authedFetch(`${FUNCTIONS_BASE}/startPftExtraction`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenantId, siteId, count }),
  });
  let body = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  if (!res.ok) throw new Error(body.error || "Couldn't start the extraction. Please try again.");
  return body;
};
